// Client-side status gating for the doctor-facing global actions ("Trigger
// refinement", "End treatment") in the Actions tab. The backend only enforces
// a floor guard (reject from a terminal status, see
// backend/src/services/workflow/transition.ts applyGlobalAction) — everything
// below is a UI-only convenience so a doctor isn't offered an action that
// can't possibly make sense for the case's current stage. Because the backend
// remains the source of truth, these predicates deliberately err on the side
// of ALLOWING when a status is unrecognized or ambiguous: a false "allow"
// just falls through to the existing modal/flow (or a 409 from the backend
// guard), while a false "block" would hide a legitimate clinical action.
//
// Status literals mirror the InternalStatus vocabulary defined in
// backend/src/services/workflow/statuses/internal/*.ts. That package can't be
// imported from the dashboard (cross-package boundary), so the relevant
// values are duplicated here — keep this file in sync if the backend adds,
// renames, or removes statuses in those macros.

import { isV2PatientVersion } from '@odo/shared-ui';

export interface PatientActionStatus {
    /** PatientMondayData.internalCaseStatus, e.g. "Tx - 1. Treatment in progress". */
    internalCaseStatus?: string | null;
    version?: string | null;
}

export interface ActionAvailability {
    allowed: boolean;
    reason?: string;
}

const ALLOWED: ActionAvailability = { allowed: true };

const CLOSED_REASON = 'This case is closed';
const NOT_STARTED_REASON = "Treatment hasn't started yet";
const ALREADY_IN_RETENTION_REASON = 'This case is already in retention';
const COMPLETED_REASON = 'A completed case cannot be reactivated';

// TerminalStatus (statuses/internal/terminal.ts).
const TERMINAL_STATUSES = new Set([
    'Term - 1. Done',
    'Term - 2. Patient declined treatment',
    'Term - 3. Archived',
    'Term - 4. Patient design expired'
]);

// Terminal statuses with NO reactivate-case edge in steps/terminal.ts. Listing
// the exceptions rather than the allowed set means a terminal status added to
// the backend defaults to offering recovery, which is the safe direction: the
// worst case is a rejected request, not a permanently stranded case.
const NON_REACTIVATABLE_TERMINAL_STATUSES = new Set(['Term - 1. Done']);

// DesignStatus + ApprovalStatus + RedesignStatus + ManufacturingStatus
// (statuses/internal/{design,approval,redesign,manufacturing}.ts) — case
// setup, submission, design, and manufacturing, all before aligners are
// delivered to the patient. "Init - 13. Pending patient appointment update"
// is the last of these: aligners are being handed over but treatment hasn't
// started yet, so it's still gated. Delivery lands the case on
// "Tx - 0. Pending Monitoring start", which is intentionally NOT in this
// set — aligners are already with the patient at that point.
const PRE_TREATMENT_STATUSES = new Set([
    // Design
    'Init - 0. Submission incomplete',
    'Init - 1. Records under review',
    'Init - 2. Create in manufacturer',
    'Init - 3. Submit for design',
    'Init - 4. Waiting for design',
    'Init - 4.1. Design pre-approved',
    'Init - 5. Review design',
    'Init - 6. Create Dx',
    'Init - 7. Review Dx',
    'Init - 7.1. Ortho review',
    // Approval
    'Init - 8. Pending approval',
    // Redesign
    'Redesign - 1. Submit for redesign',
    'Redesign - 2. Waiting for redesign',
    'Redesign - 3. Review redesign',
    'Redesign - 4. Create Dx',
    'Redesign - 5. Review Dx',
    'Redesign - 5.1. Ortho review',
    // Manufacturing
    'Init - 9. Submit for manufacturing',
    'Init - 9.1. Waiting for post-approval redesign',
    'Init - 9.2. Review post-approval redesign',
    'Init - 10. Create SDI',
    'Init - 10.1. Review SDI',
    'Init - 10.2. Submit for manufacturing',
    'Init - 11. Create patient in DM',
    'Init - 12. Waiting for manufacturing',
    'Init - 13. Pending patient appointment update'
]);

// RetentionStatus (statuses/internal/retention.ts) statuses reached once the
// case has started arranging retention — "End treatment" no longer applies
// once retention is already underway. "Ret - 0. Doctor refused retention" is
// deliberately excluded: it resumes active treatment, it isn't "in
// retention".
const ALREADY_IN_RETENTION_STATUSES = new Set([
    'Ret - 1. Ready for retention',
    'Ret - 2. Pending retention records',
    'Ret - 2.1. Retention records under review',
    'Ret - 3. Submit retention to manufacturer',
    'Ret - 4. Waiting for manufacturing',
    'Ret - 5. Pending retainers delivery'
]);

export function canEndTreatment(patient: PatientActionStatus): ActionAvailability {
    const status = patient.internalCaseStatus;
    if (!status) return ALLOWED;
    if (TERMINAL_STATUSES.has(status)) return { allowed: false, reason: CLOSED_REASON };
    if (PRE_TREATMENT_STATUSES.has(status)) return { allowed: false, reason: NOT_STARTED_REASON };
    if (ALREADY_IN_RETENTION_STATUSES.has(status)) {
        return { allowed: false, reason: ALREADY_IN_RETENTION_REASON };
    }
    return ALLOWED;
}

export function canTriggerRefinement(patient: PatientActionStatus): ActionAvailability {
    const status = patient.internalCaseStatus;
    if (!status) return ALLOWED;
    if (TERMINAL_STATUSES.has(status)) return { allowed: false, reason: CLOSED_REASON };
    if (PRE_TREATMENT_STATUSES.has(status)) return { allowed: false, reason: NOT_STARTED_REASON };
    return ALLOWED;
}

/**
 * Within the terminal domain the tile is rendered for (see
 * showsReactivateCase), this blocks only the statuses known NOT to be
 * recoverable. That keeps the file's house rule - err toward allowing - where
 * it matters most: a false block here hides the ONLY recovery path on a case
 * that is already stuck, and the practice has no admin backstop. A false allow
 * costs one rejected request and a toast.
 */
export function canReactivateCase(patient: PatientActionStatus): ActionAvailability {
    const status = patient.internalCaseStatus;
    if (status && NON_REACTIVATABLE_TERMINAL_STATUSES.has(status)) {
        return { allowed: false, reason: COMPLETED_REASON };
    }
    return ALLOWED;
}

// On V2 the orthodontist decides refinement and end of treatment; the backend 403s a GP.
export function showsOrthoDecidedActions(patient: PatientActionStatus): boolean {
    return !isV2PatientVersion(patient.version);
}

export function showsReactivateCase(patient: PatientActionStatus): boolean {
    const status = patient.internalCaseStatus;
    return !!status && TERMINAL_STATUSES.has(status);
}
